import { FC } from "react";
import { splitAndCapitalize } from "./BuildingComponents";
import { CustomInput, Table } from "taltech-styleguide";
import { useField } from "formik";
import { formatDate } from "@dirootie-extensions/javascript";

export type Props = {
  selectedType: string;
  dropdownOptions: any;
};

/**
 * Functional Component: ComponentsTableOptions
 * 
 * This component renders a table with options for the selected component type.
 * If no component type is selected, it displays a message prompting the user to select a component.
 * 
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.selectedType - The currently selected component type.
 * @param {Object} props.dropdownOptions - The dropdown options for each component type.
 * 
 * Returns:
 * @returns {JSX.Element} A JSX element containing the table with options for the selected component type.
 */

export const ComponentsTableOptions: FC<Props> = ({
  selectedType,
  dropdownOptions,
}) => {
  return (
    <div className="bg-gray-400 border py-3 px-4">
      {selectedType === "" ? (
        <>Please select a component</>
      ) : (
        <div className="w-100 overflow-auto">
          <h3>{splitAndCapitalize(selectedType)}</h3>
          <Table>
            <thead>
              <tr>
                <td className="w-50px"></td>
                <td>Name</td>
                <td>Description</td>
                <td>Created On</td>
                <td>Modified On</td>
              </tr>
            </thead>
            <tbody>
              {dropdownOptions[selectedType].map((option: any) => (
                <TableRow
                  key={option.id}
                  option={option}
                  selectedType={selectedType}
                />
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

/**
 * Functional Component: TableRow
 * 
 * This component renders a row in the components table.
 * 
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.option - The option object containing the details of the component.
 * @param {string} props.selectedType - The currently selected component type.
 * 
 * Returns:
 * @returns {JSX.Element} A JSX element containing a table row.
 */
const TableRow = ({ option, selectedType }: any) => {
  const [field, helpers, meta] = useField(`${selectedType}.options.elements`);

  return (
    <tr key={option.id}>
      <td style={{ minWidth: "30px", width: "30px" }}>
        <CustomInput
          type="checkbox"
          checked={field.value.includes(option.id)}
          onChange={(e) => {
            if (e.target.checked) {
              meta.setValue([...field.value, option.id]);
            } else {
              meta.setValue(
                field.value.filter((id: string) => id !== option.id)
              );
            }
          }}
        />
      </td>
      <td>{option.name}</td>
      <td>{option.description}</td>
      <td>{formatDate(option.createdDate)}</td>
      <td>{formatDate(option.modifiedDate)}</td>
    </tr>
  );
};
